import {
  EVENT_TYPES,
  NODE_COPY_KEYS,
  ORBITAL_COPY_KEYS,
  type CartographyLabelPlacement,
  type CartographyLabelTier,
  type CelestialArchetype,
  type MeshSnapshot,
  type NexusEvent,
  type NexusEventSeverity,
  type NexusRelation,
  type NodeCluster,
  type NodeKind,
  type NodeStatus,
  type OrbitalKind,
  type OrbitalTone,
} from "./mesh";

export class SchemaError extends Error {
  constructor(
    public readonly path: string,
    message: string,
  ) {
    super(`${path}: ${message}`);
    this.name = "SchemaError";
  }
}

const NODE_KINDS = ["system", "entity", "artifact", "location", "goal"] as const satisfies readonly NodeKind[];
const NODE_STATUSES = [
  "online",
  "offline",
  "active",
  "building",
  "draft",
  "warning",
] as const satisfies readonly NodeStatus[];
const NODE_CLUSTERS = [
  "core",
  "projects",
  "memory",
  "infrastructure",
  "life",
] as const satisfies readonly NodeCluster[];
const ORBITAL_KINDS = [
  "service",
  "runtime",
  "storage",
  "integration",
  "artifact",
  "library",
] as const satisfies readonly OrbitalKind[];
const RELATIONS = [
  "depends_on",
  "presents",
  "tracks",
  "contains",
  "relates_to",
  "drives",
  "located_at",
] as const satisfies readonly NexusRelation[];
const EVENT_SEVERITIES = ["info", "success", "warning"] as const satisfies readonly NexusEventSeverity[];
const ARCHETYPES = ["stellar", "black-hole"] as const satisfies readonly CelestialArchetype[];
const ORBITAL_TONES = ["core", "infra", "memory", "compute", "signal"] as const satisfies readonly OrbitalTone[];
const LABEL_TIERS = ["hero", "major", "minor"] as const satisfies readonly CartographyLabelTier[];
const LABEL_PLACEMENTS = [
  "south",
  "north",
  "east",
  "west",
  "south-east",
  "south-west",
  "north-east",
  "north-west",
] as const satisfies readonly CartographyLabelPlacement[];

function record(value: unknown, path: string): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value))
    throw new SchemaError(path, "expected object");
  return value as Record<string, unknown>;
}

function string(value: unknown, path: string): string {
  if (typeof value !== "string") throw new SchemaError(path, "expected string");
  return value;
}

function nonEmptyString(value: unknown, path: string): string {
  const result = string(value, path);
  if (!result.trim()) throw new SchemaError(path, "expected non-empty string");
  return result;
}

function finiteNumber(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) throw new SchemaError(path, "expected finite number");
  return value;
}

function integer(value: unknown, path: string): number {
  const result = finiteNumber(value, path);
  if (!Number.isInteger(result)) throw new SchemaError(path, "expected integer");
  return result;
}

function array(value: unknown, path: string): unknown[] {
  if (!Array.isArray(value)) throw new SchemaError(path, "expected array");
  return value;
}

function enumValue<T extends string>(value: unknown, values: readonly T[], path: string): T {
  const result = string(value, path);
  if (!values.includes(result as T)) throw new SchemaError(path, `unsupported value ${result}`);
  return result as T;
}

function optionalString(value: unknown, path: string): string | undefined {
  return value === undefined ? undefined : string(value, path);
}

function isoDate(value: unknown, path: string): string {
  const result = string(value, path);
  if (Number.isNaN(Date.parse(result))) throw new SchemaError(path, "expected ISO-compatible date string");
  return result;
}

function assertUniqueIds(items: Array<{ id: string }>, path: string) {
  const seen = new Set<string>();
  for (const [index, item] of items.entries()) {
    if (seen.has(item.id)) throw new SchemaError(`${path}[${index}].id`, `duplicate id ${item.id}`);
    seen.add(item.id);
  }
}

function decodeMetrics(value: unknown, path: string) {
  if (value === undefined) return undefined;
  const item = record(value, path);
  const result: NonNullable<MeshSnapshot["nodes"][number]["metrics"]> = {};
  if (item.progress !== undefined) {
    const progress = finiteNumber(item.progress, `${path}.progress`);
    if (progress < 0 || progress > 100) throw new SchemaError(`${path}.progress`, "expected value in range 0..100");
    result.progress = progress;
  }
  if (item.count !== undefined) {
    const count = integer(item.count, `${path}.count`);
    if (count < 0) throw new SchemaError(`${path}.count`, "expected non-negative integer");
    result.count = count;
  }
  if (item.version !== undefined) result.version = nonEmptyString(item.version, `${path}.version`);
  if (item.updatedAt !== undefined) result.updatedAt = isoDate(item.updatedAt, `${path}.updatedAt`);
  if (item.meta !== undefined) result.meta = nonEmptyString(item.meta, `${path}.meta`);
  return result;
}

function decodeNode(value: unknown, path: string): MeshSnapshot["nodes"][number] {
  const item = record(value, path);
  const href = optionalString(item.href, `${path}.href`);
  if (href !== undefined) {
    try {
      new URL(href);
    } catch {
      throw new SchemaError(`${path}.href`, "expected absolute URL");
    }
  }
  return {
    id: nonEmptyString(item.id, `${path}.id`),
    copyKey: enumValue(item.copyKey, NODE_COPY_KEYS, `${path}.copyKey`),
    kind: enumValue(item.kind, NODE_KINDS, `${path}.kind`),
    status: enumValue(item.status, NODE_STATUSES, `${path}.status`),
    cluster: enumValue(item.cluster, NODE_CLUSTERS, `${path}.cluster`),
    ...(href === undefined ? {} : { href }),
    ...(item.metrics === undefined ? {} : { metrics: decodeMetrics(item.metrics, `${path}.metrics`) }),
  };
}

function decodeOrbital(value: unknown, path: string): MeshSnapshot["orbitals"][number] {
  const item = record(value, path);
  return {
    id: nonEmptyString(item.id, `${path}.id`),
    parentId: nonEmptyString(item.parentId, `${path}.parentId`),
    copyKey: enumValue(item.copyKey, ORBITAL_COPY_KEYS, `${path}.copyKey`),
    kind: enumValue(item.kind, ORBITAL_KINDS, `${path}.kind`),
    status: enumValue(item.status, NODE_STATUSES, `${path}.status`),
    cluster: enumValue(item.cluster, NODE_CLUSTERS, `${path}.cluster`),
    ...(item.metrics === undefined ? {} : { metrics: decodeMetrics(item.metrics, `${path}.metrics`) }),
  };
}

function decodeEdge(value: unknown, path: string): MeshSnapshot["edges"][number] {
  const item = record(value, path);
  const strength = finiteNumber(item.strength, `${path}.strength`);
  if (strength < 0 || strength > 1) throw new SchemaError(`${path}.strength`, "expected value in range 0..1");
  return {
    id: nonEmptyString(item.id, `${path}.id`),
    source: nonEmptyString(item.source, `${path}.source`),
    target: nonEmptyString(item.target, `${path}.target`),
    relation: enumValue(item.relation, RELATIONS, `${path}.relation`),
    strength,
  };
}

function decodeEvent(value: unknown, path: string): NexusEvent {
  const item = record(value, path);
  const payload = item.payload === undefined ? undefined : record(item.payload, `${path}.payload`);
  if (payload) {
    for (const [key, payloadValue] of Object.entries(payload)) {
      if (!["string", "number", "boolean"].includes(typeof payloadValue)) {
        throw new SchemaError(`${path}.payload.${key}`, "expected string, number, or boolean");
      }
      if (typeof payloadValue === "number" && !Number.isFinite(payloadValue)) {
        throw new SchemaError(`${path}.payload.${key}`, "expected finite number");
      }
    }
  }
  const targetNodeId = optionalString(item.targetNodeId, `${path}.targetNodeId`);
  return {
    id: nonEmptyString(item.id, `${path}.id`),
    type: enumValue(item.type, EVENT_TYPES, `${path}.type`),
    sourceNodeId: nonEmptyString(item.sourceNodeId, `${path}.sourceNodeId`),
    ...(targetNodeId === undefined ? {} : { targetNodeId }),
    timestamp: isoDate(item.timestamp, `${path}.timestamp`),
    severity: enumValue(item.severity, EVENT_SEVERITIES, `${path}.severity`),
    ...(payload === undefined ? {} : { payload: payload as NexusEvent["payload"] }),
  };
}

function decodeVector3(value: unknown, path: string): [number, number, number] {
  const items = array(value, path);
  if (items.length !== 3) throw new SchemaError(path, "expected 3-item vector");
  return [
    finiteNumber(items[0], `${path}[0]`),
    finiteNumber(items[1], `${path}[1]`),
    finiteNumber(items[2], `${path}[2]`),
  ];
}

function decodeNodeProjection(value: unknown, path: string): MeshSnapshot["projection"]["nodes"][string] {
  const item = record(value, path);
  const magnitude = finiteNumber(item.magnitude, `${path}.magnitude`);
  if (magnitude <= 0) throw new SchemaError(`${path}.magnitude`, "expected positive value");
  const cartography = record(item.cartography, `${path}.cartography`);
  const labelOffsetX =
    cartography.labelOffsetX === undefined
      ? undefined
      : finiteNumber(cartography.labelOffsetX, `${path}.cartography.labelOffsetX`);
  const labelOffsetY =
    cartography.labelOffsetY === undefined
      ? undefined
      : finiteNumber(cartography.labelOffsetY, `${path}.cartography.labelOffsetY`);
  return {
    position: decodeVector3(item.position, `${path}.position`),
    magnitude,
    archetype: enumValue(item.archetype, ARCHETYPES, `${path}.archetype`),
    cartography: {
      labelTier: enumValue(cartography.labelTier, LABEL_TIERS, `${path}.cartography.labelTier`),
      labelPlacement: enumValue(cartography.labelPlacement, LABEL_PLACEMENTS, `${path}.cartography.labelPlacement`),
      ...(labelOffsetX === undefined ? {} : { labelOffsetX }),
      ...(labelOffsetY === undefined ? {} : { labelOffsetY }),
    },
  };
}

function decodeEdgeProjection(value: unknown, path: string): MeshSnapshot["projection"]["edges"][string] {
  const item = record(value, path);
  const thickness = finiteNumber(item.thickness, `${path}.thickness`);
  const opacityScale = finiteNumber(item.opacityScale, `${path}.opacityScale`);
  if (thickness <= 0) throw new SchemaError(`${path}.thickness`, "expected positive value");
  if (opacityScale <= 0) throw new SchemaError(`${path}.opacityScale`, "expected positive value");
  return {
    bend: finiteNumber(item.bend, `${path}.bend`),
    skew: finiteNumber(item.skew, `${path}.skew`),
    lift: finiteNumber(item.lift, `${path}.lift`),
    thickness,
    opacityScale,
  };
}

function decodeOrbitalProjection(value: unknown, path: string): MeshSnapshot["projection"]["orbitals"][string] {
  const item = record(value, path);
  const orbit = record(item.orbit, `${path}.orbit`);
  const visual = record(item.visual, `${path}.visual`);
  const radius = finiteNumber(orbit.radius, `${path}.orbit.radius`);
  const eccentricity = finiteNumber(orbit.eccentricity, `${path}.orbit.eccentricity`);
  const ringOpacity =
    orbit.ringOpacity === undefined ? undefined : finiteNumber(orbit.ringOpacity, `${path}.orbit.ringOpacity`);
  const scale = finiteNumber(visual.scale, `${path}.visual.scale`);
  const ringCount = integer(visual.ringCount, `${path}.visual.ringCount`);
  const glow = finiteNumber(visual.glow, `${path}.visual.glow`);
  if (radius <= 0) throw new SchemaError(`${path}.orbit.radius`, "expected positive radius");
  if (eccentricity < 0 || eccentricity >= 1)
    throw new SchemaError(`${path}.orbit.eccentricity`, "expected value in range 0..<1");
  if (ringOpacity !== undefined && (ringOpacity < 0 || ringOpacity > 1))
    throw new SchemaError(`${path}.orbit.ringOpacity`, "expected value in range 0..1");
  if (scale <= 0) throw new SchemaError(`${path}.visual.scale`, "expected positive scale");
  if (ringCount < 0 || ringCount > 8)
    throw new SchemaError(`${path}.visual.ringCount`, "expected integer in range 0..8");
  if (glow < 0) throw new SchemaError(`${path}.visual.glow`, "expected non-negative value");
  return {
    orbit: {
      radius,
      speed: finiteNumber(orbit.speed, `${path}.orbit.speed`),
      tiltX: finiteNumber(orbit.tiltX, `${path}.orbit.tiltX`),
      tiltZ: finiteNumber(orbit.tiltZ, `${path}.orbit.tiltZ`),
      eccentricity,
      phase: finiteNumber(orbit.phase, `${path}.orbit.phase`),
      ...(ringOpacity === undefined ? {} : { ringOpacity }),
    },
    visual: {
      scale,
      ringCount,
      glow,
      tone: enumValue(visual.tone, ORBITAL_TONES, `${path}.visual.tone`),
    },
  };
}

function assertReferentialIntegrity(snapshot: MeshSnapshot) {
  const nodeIds = new Set(snapshot.nodes.map((node) => node.id));
  const orbitalIds = new Set(snapshot.orbitals.map((orbital) => orbital.id));

  for (const [index, edge] of snapshot.edges.entries()) {
    if (!nodeIds.has(edge.source))
      throw new SchemaError(`snapshot.edges[${index}].source`, `unknown node ${edge.source}`);
    if (!nodeIds.has(edge.target))
      throw new SchemaError(`snapshot.edges[${index}].target`, `unknown node ${edge.target}`);
  }
  for (const [index, orbital] of snapshot.orbitals.entries()) {
    if (!nodeIds.has(orbital.parentId))
      throw new SchemaError(`snapshot.orbitals[${index}].parentId`, `unknown parent node ${orbital.parentId}`);
  }
  for (const [index, event] of snapshot.events.entries()) {
    if (!nodeIds.has(event.sourceNodeId))
      throw new SchemaError(`snapshot.events[${index}].sourceNodeId`, `unknown node ${event.sourceNodeId}`);
    if (event.targetNodeId && !nodeIds.has(event.targetNodeId))
      throw new SchemaError(`snapshot.events[${index}].targetNodeId`, `unknown node ${event.targetNodeId}`);
  }

  const nodeProjectionIds = new Set(Object.keys(snapshot.projection.nodes));
  const edgeProjectionIds = new Set(Object.keys(snapshot.projection.edges));
  const edgeIds = new Set(snapshot.edges.map((edge) => edge.id));
  const orbitalProjectionIds = new Set(Object.keys(snapshot.projection.orbitals));
  for (const nodeId of nodeIds)
    if (!nodeProjectionIds.has(nodeId))
      throw new SchemaError(`snapshot.projection.nodes.${nodeId}`, "missing projection");
  for (const projectionId of nodeProjectionIds)
    if (!nodeIds.has(projectionId))
      throw new SchemaError(`snapshot.projection.nodes.${projectionId}`, "projection has no matching node");
  for (const edgeId of edgeIds)
    if (!edgeProjectionIds.has(edgeId))
      throw new SchemaError(`snapshot.projection.edges.${edgeId}`, "missing projection");
  for (const projectionId of edgeProjectionIds)
    if (!edgeIds.has(projectionId))
      throw new SchemaError(`snapshot.projection.edges.${projectionId}`, "projection has no matching edge");
  for (const orbitalId of orbitalIds)
    if (!orbitalProjectionIds.has(orbitalId))
      throw new SchemaError(`snapshot.projection.orbitals.${orbitalId}`, "missing projection");
  for (const projectionId of orbitalProjectionIds)
    if (!orbitalIds.has(projectionId))
      throw new SchemaError(`snapshot.projection.orbitals.${projectionId}`, "projection has no matching orbital");
}

export function decodeMeshSnapshot(value: unknown): MeshSnapshot {
  const root = record(value, "snapshot");
  if (root.schemaVersion !== 2) throw new SchemaError("snapshot.schemaVersion", "expected 2");
  const rawNodes = array(root.nodes, "snapshot.nodes");
  const rawOrbitals = array(root.orbitals, "snapshot.orbitals");
  const rawEdges = array(root.edges, "snapshot.edges");
  const rawEvents = array(root.events, "snapshot.events");
  const rawProjection = record(root.projection, "snapshot.projection");
  const rawNodeProjection = record(rawProjection.nodes, "snapshot.projection.nodes");
  const rawEdgeProjection = record(rawProjection.edges, "snapshot.projection.edges");
  const rawOrbitalProjection = record(rawProjection.orbitals, "snapshot.projection.orbitals");

  const nodes = rawNodes.map((item, index) => decodeNode(item, `snapshot.nodes[${index}]`));
  const orbitals = rawOrbitals.map((item, index) => decodeOrbital(item, `snapshot.orbitals[${index}]`));
  const edges = rawEdges.map((item, index) => decodeEdge(item, `snapshot.edges[${index}]`));
  const events = rawEvents.map((item, index) => decodeEvent(item, `snapshot.events[${index}]`));
  assertUniqueIds(nodes, "snapshot.nodes");
  assertUniqueIds(orbitals, "snapshot.orbitals");
  assertUniqueIds(edges, "snapshot.edges");
  assertUniqueIds(events, "snapshot.events");

  const projection = {
    nodes: Object.fromEntries(
      Object.entries(rawNodeProjection).map(([id, item]) => [
        id,
        decodeNodeProjection(item, `snapshot.projection.nodes.${id}`),
      ]),
    ),
    edges: Object.fromEntries(
      Object.entries(rawEdgeProjection).map(([id, item]) => [
        id,
        decodeEdgeProjection(item, `snapshot.projection.edges.${id}`),
      ]),
    ),
    orbitals: Object.fromEntries(
      Object.entries(rawOrbitalProjection).map(([id, item]) => [
        id,
        decodeOrbitalProjection(item, `snapshot.projection.orbitals.${id}`),
      ]),
    ),
  };

  const snapshot: MeshSnapshot = {
    schemaVersion: 2,
    generatedAt: isoDate(root.generatedAt, "snapshot.generatedAt"),
    revision: nonEmptyString(root.revision, "snapshot.revision"),
    nodes,
    orbitals,
    edges,
    events,
    projection,
  };
  assertReferentialIntegrity(snapshot);
  return snapshot;
}

export function decodeNexusEvent(value: unknown): NexusEvent {
  return decodeEvent(value, "event");
}
